import { apiClient as api } from "./httpClient";

// Agenda do dia (RN-546): sessões do período com contato de WhatsApp e mensagem já montada pelo
// servidor com o modelo escolhido (ou o padrão). O clique em Enviar/Copiar é registrado para a
// sessão constar como "já enviado". Check-in (RN-547): página pública, anônima, alias + token.

export interface DayScheduleRow {
  scheduleId: string;
  /** ISO no relógio da entidade */
  start: string;
  end: string;
  patientId: string | null;
  patientName: string | null;
  responsibleName: string | null;
  /** só dígitos com DDI (E.164), pronto para wa.me; nulo = sem telefone */
  phone: string | null;
  professionalId: string | null;
  professionalName: string | null;
  specialty: string | null;
  serviceName: string | null;
  confirmation: string | null;
  presence: string | null;
  message: string | null;
  checkinUrl: string | null;
  sentCount: number;
  lastSentAt: string | null;
  lastSentBy: string | null;
  /** Significado da confirmação (RN-546a): a cor da tag sai daqui, não do nome do status */
  confirmationKind: "confirmed" | "cancelled" | "pending";
  presenceKind: "attended" | "no_show" | null;
  paid: boolean;
  /** Pagamento pelo caixa: abre o recibo. Nulo com baixa feita direto na agenda. */
  paymentId: string | null;
  /** Tipo/plano (RN-546d): "Particular" ou o nome do plano do serviço de convênio; nulo sem serviço. */
  planLabel: string | null;
}

/** Resumo em cards (RN-546a), sem pacientes de teste. */
export interface DayScheduleSummary {
  patients: number;
  sessions: number;
  confirmed: number;
  pending: number;
  cancelled: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  cancelledByClinic: number;
  attended: number;
  noShow: number;
  withoutPresence: number;
  /** com baixa, inclusive pagamento antecipado */
  paid: number;
  /** presença que cobra, sem baixa (régua do caixa, RN-543a) */
  unpaid: number;
}

export interface DayScheduleTemplate { id: string; name: string; isDefault: boolean; }
export interface DayScheduleOption { id: string; name: string; }

export interface DaySchedule {
  from: string;
  to: string;
  items: DayScheduleRow[];
  templates: DayScheduleTemplate[];
  professionals: DayScheduleOption[];
  summary: DayScheduleSummary;
}

export interface DayScheduleFilters {
  from?: string;
  to?: string;
  professionalId?: string;
  templateId?: string;
  clinic?: string;
}

export interface CheckinInfo {
  status: "valid" | "confirmed" | "cancelled" | "expired" | "invalid" | "gone";
  clinicName: string | null;
  patientFirstName: string | null;
  date: string | null;
  weekday: string | null;
  start: string | null;
  end: string | null;
  professionalName: string | null;
  specialty: string | null;
  confirmedAt: string | null;
  /** RN-547a: pendente ou confirmada, e a sessão ainda não começou */
  canCancel: boolean;
  cancelledAt: string | null;
}

const query = (filters: DayScheduleFilters): string => {
  const q = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
  const s = q.toString();
  return s ? `?${s}` : "";
};

export const dayScheduleService = {
  get: async (filters: DayScheduleFilters): Promise<DaySchedule> => {
    const response = await api.get(`/day-schedule${query(filters)}`, { timeout: 60000 });
    return response.data;
  },

  exportXlsx: async (filters: DayScheduleFilters): Promise<Blob> => {
    const response = await api.get(`/day-schedule/xlsx${query({ from: filters.from, to: filters.to, professionalId: filters.professionalId })}`, { responseType: "blob", timeout: 60000 });
    return response.data;
  },

  registerSend: async (scheduleId: string, templateId: string | null, phone: string, renderedBody: string): Promise<void> => {
    await api.post("/day-schedule/sends", { scheduleId, templateId, phone, renderedBody });
  },
};

export const checkinService = {
  get: async (alias: string, token: string): Promise<CheckinInfo> => {
    const response = await api.get(`/checkin/${encodeURIComponent(alias)}/${encodeURIComponent(token)}`);
    return response.data;
  },

  confirm: async (alias: string, token: string): Promise<CheckinInfo> => {
    const response = await api.post(`/checkin/${encodeURIComponent(alias)}/${encodeURIComponent(token)}/confirm`);
    return response.data;
  },

  cancel: async (alias: string, token: string, reason: string): Promise<CheckinInfo> => {
    const response = await api.post(`/checkin/${encodeURIComponent(alias)}/${encodeURIComponent(token)}/cancel`, { reason: reason.trim() || null });
    return response.data;
  },
};
